/* eslint-disable no-plusplus,react/jsx-pascal-case */
import PropTypes from 'prop-types';
import React from 'react';
import { getExperimentID } from 'ctm-app/client/store/selectors';
import { pageContext } from 'ctm-util-functions';
import { getHasSavedResults } from 'ctm-selectors/selectors/results';
import { useSelector } from 'react-redux';
import { getFeatureFlag } from 'ctm-selectors/selectors/base';
import { SimpleFlexbox } from '../../../../flexbox/flexbox';
import { experimentIDEnum } from '../../../../../../ctm-vertical-base/base/reducers/page';

/**
 * Main layout component for rendering a results row
 * @param {Object} props - The components props
 * @param {Array} props.xs - componentLists for xs breakpoint and above
 * @param {Array} props.sm - componentLists for sm breakpoint and above
 * @param {Array} props.xxl - componentLists for xxl breakpoint
 * @param {Object} props.stateProps - The stateful product object passed in
 * @returns {Node} The row layout component
 * @constructor
 */
const LayoutComparableResultsRow = ({ xs, sm, xxl, stateProps }) => {
    const { breakpoint } = pageContext.getProps().pageState;
    const experimentID = useSelector(getExperimentID);

    switch (breakpoint) {
        case 'xs':
            return <_Xs componentLists={xs} stateProps={stateProps} />;
        case 'sm':
        case 'md':
        case 'lg':
        case 'xl':
            return <_Sm componentLists={sm} stateProps={stateProps} />;
        // XXL breakpoint
        default: {
            if (experimentID === experimentIDEnum.CTM_REBRAND) {
                return <_Sm componentLists={sm} stateProps={stateProps} />;
            }
            return <_Xxl componentLists={xxl} stateProps={stateProps} />;
        }
    }
};

LayoutComparableResultsRow.propTypes = {
    xs: PropTypes.arrayOf(PropTypes.array),
    sm: PropTypes.arrayOf(PropTypes.array),
    xxl: PropTypes.arrayOf(PropTypes.array),
    stateProps: PropTypes.object
};

/**
 * Private component to render layout for XS breakpoint and up
 * @param {Object} props - The components props
 * @param {Array} props.componentLists - all sub component lists to be rendered under this layout
 * @param {Object} props.stateProps - The stateful product object passed in
 * @returns {_Xs} Xs layout component
 * @private
 */
const _Xs = ({ componentLists, stateProps }) => {
    let i = 0;
    const { constructComponents } = pageContext.getProps().director;

    return (
        <SimpleFlexbox>
            <SimpleFlexbox border="all" bgColor="brand_base_0" margin="bottom">
                <SimpleFlexbox display="flex" variant="flex_grow_cell" margin="bottom">
                    <SimpleFlexbox column display="flex" padding="left_small" itemsCenter="xs">
                        {constructComponents(componentLists[i++], { stateProps })}
                    </SimpleFlexbox>
                    <SimpleFlexbox column flex={1} display="flex" padding={['right_small', 'top']} margin="left_small" contentBetween="xs">
                        <SimpleFlexbox display="flex" itemsCenter="xs">
                            {constructComponents(componentLists[i++], { stateProps })}
                        </SimpleFlexbox>
                        <SimpleFlexbox column display="flex" padding="top_xsmall">
                            {constructComponents(componentLists[i++], { stateProps })}
                        </SimpleFlexbox>
                        <SimpleFlexbox display="flex" padding="top_xsmall">
                            {constructComponents(componentLists[i++], { stateProps })}
                        </SimpleFlexbox>
                        <SimpleFlexbox display="flex" padding="top_xsmall" contentAround="xs" itemsCenter="xs">
                            {constructComponents(componentLists[i++], { stateProps })}
                        </SimpleFlexbox>
                        <SimpleFlexbox>
                            {constructComponents(componentLists[i++], { stateProps })}
                        </SimpleFlexbox>
                    </SimpleFlexbox>
                </SimpleFlexbox>
                <SimpleFlexbox>
                    {constructComponents(componentLists[i++], { stateProps })}
                </SimpleFlexbox>
            </SimpleFlexbox>
            <SimpleFlexbox border="left_right">
                {constructComponents(componentLists[i], { stateProps })}
            </SimpleFlexbox>
        </SimpleFlexbox>
    );
};

_Xs.propTypes = {
    componentLists: PropTypes.arrayOf(PropTypes.array),
    stateProps: PropTypes.object
};

/**
 * Private component to render layout for SM breakpoint and up
 * @param {Object} props - The components props
 * @param {Array} props.componentLists - all sub component lists to be rendered under this layout
 * @param {Object} props.stateProps - The stateful product object passed in
 * @returns {_Sm} sm layout component
 * @private
 */
const _Sm = ({ componentLists, stateProps }) => {
    let i = 0;
    const { constructComponents } = pageContext.getProps().director;
    return (
        <SimpleFlexbox>
            <SimpleFlexbox row md={9} xl={10} border="all" bgColor="brand_base_0" variant="resultsRow" margin="bottom">
                <SimpleFlexbox column sm={2} display="flex" itemsCenter="sm" contentCenter="sm" variant="text_align_center">
                    {constructComponents(componentLists[i++], { stateProps })}
                </SimpleFlexbox>
                <SimpleFlexbox column xs={6} display="flex" contentCenter="sm" padding="left_right">
                    <SimpleFlexbox column flex={3} display="flex" contentAround="sm" padding="top">
                        {constructComponents(componentLists[i++], { stateProps })}
                    </SimpleFlexbox>
                    <SimpleFlexbox flex={1} display="flex" border="top" padding="top_bottom" itemsCenter="sm">
                        {constructComponents(componentLists[i++], { stateProps })}
                    </SimpleFlexbox>
                </SimpleFlexbox>
                <SimpleFlexbox column flex={1} display="flex" gapless="xs" border={false} contentCenter="sm">
                    <SimpleFlexbox display="flex" contentAround="sm" padding="top_bottom" itemsCenter="sm">
                        {constructComponents(componentLists[i++], { stateProps })}
                    </SimpleFlexbox>
                    <SimpleFlexbox padding="bottom_right">
                        {constructComponents(componentLists[i++], { stateProps })}
                    </SimpleFlexbox>
                    <SimpleFlexbox>
                        {constructComponents(componentLists[i++], { stateProps })}
                    </SimpleFlexbox>
                </SimpleFlexbox>
            </SimpleFlexbox>
            <SimpleFlexbox md={9} xl={10} border="left_right">
                {constructComponents(componentLists[i], { stateProps })}
            </SimpleFlexbox>
        </SimpleFlexbox>
    );
};

_Sm.propTypes = {
    componentLists: PropTypes.arrayOf(PropTypes.array),
    stateProps: PropTypes.object
};

/**
 * Private component to render layout for XXL breakpoint
 * @param {Object} props - The components props
 * @param {Array} props.componentLists - all sub component lists to be rendered under this layout
 * @param {Object} props.stateProps - The stateful product object passed in
 * @returns {_Xxl} XXL layout component
 * @private
 */
const _Xxl = ({ componentLists, stateProps }) => {
    let i = 0;
    const { constructComponents } = pageContext.getProps().director;
    const hasSavedResults = getHasSavedResults() || getFeatureFlag('prefillHasSavedResults');

    return (
        <SimpleFlexbox>
            <SimpleFlexbox row border="all" bgColor="brand_base_0" variant="resultsRow" margin="bottom">
                {/* We need to hide some elements if the user has a Saved results page, the code below will hide the wrapper for the compare box */}
                { hasSavedResults ? (
                    constructComponents(componentLists[i++], { stateProps })
                ) : (
                    <SimpleFlexbox display="flex" xxl={1} padding="all" itemsCenter="lg" contentCenter="lg" border="right">
                        {constructComponents(componentLists[i++], { stateProps })}
                    </SimpleFlexbox>
                )}
                <SimpleFlexbox column lg={hasSavedResults ? 2 : 1} display="flex" padding="left_right_small" itemsCenter="lg" contentCenter="lg" variant="text_align_center">
                    {constructComponents(componentLists[i++], { stateProps })}
                </SimpleFlexbox>
                <SimpleFlexbox column xs={4} display="flex" padding="all" contentAround="lg" border="right">
                    {constructComponents(componentLists[i++], { stateProps })}
                </SimpleFlexbox>
                <SimpleFlexbox column display="flex" padding="all" contentBetween="lg" border="right">
                    {constructComponents(componentLists[i++], { stateProps })}
                </SimpleFlexbox>
                <SimpleFlexbox column flex={1} display="flex" padding="all" gapless="xs" border={false}>
                    <SimpleFlexbox flex={2} display="flex" padding="all" itemsCenter="lg">
                        <SimpleFlexbox xs={7} display="flex" itemsCenter="lg" contentAround="lg" gapless="xs">
                            {constructComponents(componentLists[i++], { stateProps })}
                        </SimpleFlexbox>
                        <SimpleFlexbox xs={5} gapless="xs">
                            {constructComponents(componentLists[i++], { stateProps })}
                        </SimpleFlexbox>
                    </SimpleFlexbox>
                    <SimpleFlexbox>
                        {constructComponents(componentLists[i++], { stateProps })}
                    </SimpleFlexbox>
                </SimpleFlexbox>
            </SimpleFlexbox>
            <SimpleFlexbox lg={12} border="left_right">
                {constructComponents(componentLists[i], { stateProps })}
            </SimpleFlexbox>
        </SimpleFlexbox>
    );
};

_Xxl.propTypes = {
    componentLists: PropTypes.arrayOf(PropTypes.array),
    stateProps: PropTypes.object
};

export { LayoutComparableResultsRow };
// exported for uni test
export { _Xs, _Sm, _Xxl };
